/**
 * Shared product rail loader for Zavia homepage shelves + flash deals.
 * One file instead of duplicated inline scripts per section.
 */
(() => {
  const CURRENCY = (window.Theme && Theme.uae && Theme.uae.currency) || 'AED';
  const ROOT_MARGIN = '180px 0px';

  const money = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n.toFixed(2) : '0.00';
  };

  const toWebp = (url) => {
    if (!url) return '';
    const base = String(url).split('?')[0];
    return `${base}?width=360&height=360&crop=center&format=webp`;
  };

  const imgUrl = (p) => {
    const i0 = p.images && p.images[0];
    if (!i0) return toWebp(p.featured_image || '');
    if (typeof i0 === 'string') return toWebp(i0);
    return toWebp(i0.src || '');
  };

  const esc = (s) =>
    String(s || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/"/g, '&quot;');

  const cardHtml = (p, { compactCompare = true } = {}) => {
    const v = (p.variants && p.variants[0]) || {};
    const img = imgUrl(p);
    const price = v.price != null ? money(v.price) : '0.00';
    const compare = v.compare_at_price ? money(v.compare_at_price) : null;
    let off = '';
    let was = '';
    let disc = '';
    if (compare && Number(compare) > Number(price)) {
      const pct = Math.round((1 - Number(price) / Number(compare)) * 100);
      if (compactCompare) {
        off = `<span class="nCard__off">${pct}% Off</span>`;
        was = `<div class="nCard__was">Was: <s>${CURRENCY} ${compare}</s></div>`;
      }
      disc = `<span class="nCard__disc">${pct}% OFF</span>`;
    }
    const brand = esc(p.vendor || 'Zavia');
    const vid = v.id || '';
    return `<div class="nShelf__item nFlash__item"><article class="nCard">
      <button type="button" class="nCard__wish" data-zavia-wish="${p.id || ''}" aria-label="Wishlist">♡</button>
      <a class="nCard__a" href="/products/${p.handle}">
        <div class="nCard__media">${disc}${
          img
            ? `<img class="nCard__img" src="${esc(img)}" alt="${esc(p.title)}" loading="lazy" decoding="async" width="360" height="360">`
            : ''
        }</div>
        <div class="nCard__info">
          <p class="nCard__express"><span>cod</span></p>
          <p class="nCard__brand">${brand}</p>
          <h3 class="nCard__title">${esc(p.title)}</h3>
          <div class="nCard__priceWrap"><div class="nCard__priceLine"><strong class="nCard__price"><em>${CURRENCY}</em> ${price}</strong>${off}</div>${was}</div>
          <div class="nCard__rating"><span class="nCard__stars">★ 4.5</span><span class="nCard__reviews">(12)</span></div>
          <p class="nCard__eta">Get it in 2–3 days</p>
        </div>
      </a>
      ${
        vid
          ? `<form class="nCard__form" action="/cart/add" method="post"><input type="hidden" name="id" value="${vid}"><button type="submit" class="nCard__add"><span class="nCard__addPlus">+</span><span class="nCard__addText">Add to Cart</span></button></form>`
          : ''
      }
    </article></div>`;
  };

  const bindScroller = (scroller, prevBtn, nextBtn) => {
    if (!scroller) return () => {};
    const syncArrows = () => {
      if (!prevBtn || !nextBtn) return;
      const max = scroller.scrollWidth - scroller.clientWidth;
      const canScroll = max > 8;
      prevBtn.hidden = !canScroll;
      nextBtn.hidden = !canScroll;
      if (!canScroll) return;
      prevBtn.disabled = scroller.scrollLeft <= 4;
      nextBtn.disabled = scroller.scrollLeft >= max - 4;
    };
    const scrollByPage = (dir) => {
      const amount = Math.max(scroller.clientWidth * 0.75, 160);
      scroller.scrollBy({ left: dir * amount, behavior: 'smooth' });
    };
    prevBtn?.addEventListener('click', () => scrollByPage(-1));
    nextBtn?.addEventListener('click', () => scrollByPage(1));
    scroller.addEventListener('scroll', syncArrows, { passive: true });
    window.addEventListener('resize', syncArrows);
    return syncArrows;
  };

  const whenVisible = (el, cb) => {
    if (!('IntersectionObserver' in window)) {
      cb();
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect();
          cb();
        }
      },
      { rootMargin: ROOT_MARGIN }
    );
    io.observe(el);
  };

  const loadProducts = (handle, limit) =>
    fetch(`/collections/${handle}/products.json?limit=${limit}`).then((r) => r.json());

  const initShelf = (el) => {
    if (el.dataset.nRailInit === '1') return;
    el.dataset.nRailInit = '1';
    const scroller = el.querySelector('[data-n-shelf-scroller]');
    const track = el.querySelector('[data-n-shelf-track]');
    const prevBtn = el.querySelector('[data-n-shelf-prev]');
    const nextBtn = el.querySelector('[data-n-shelf-next]');
    if (!scroller || !track) return;
    const syncArrows = bindScroller(scroller, prevBtn, nextBtn);
    const limit = parseInt(el.dataset.limit || '12', 10);
    const handle = el.dataset.handle || 'all';

    whenVisible(el, () => {
      if (el.dataset.loaded === '1') return;
      el.dataset.loaded = '1';
      loadProducts(handle, limit)
        .then((data) => {
          const products = data.products || [];
          track.setAttribute('aria-busy', 'false');
          if (!products.length) {
            track.innerHTML = '';
            el.hidden = true;
            syncArrows();
            return;
          }
          el.hidden = false;
          track.innerHTML = products
            .slice(0, limit)
            .map((p) => cardHtml(p, { compactCompare: true }))
            .join('');
          requestAnimationFrame(syncArrows);
        })
        .catch(() => {
          el.dataset.loaded = '0';
          track.setAttribute('aria-busy', 'false');
        });
    });
  };

  const initFlash = (root) => {
    if (root.dataset.nRailInit === '1') return;
    root.dataset.nRailInit = '1';
    const scroller = root.querySelector('[data-n-flash-scroller]');
    const track = root.querySelector('[data-n-flash-track]');
    const prevBtn = root.querySelector('[data-n-flash-prev]');
    const nextBtn = root.querySelector('[data-n-flash-next]');
    if (!track || !scroller) return;
    const syncArrows = bindScroller(scroller, prevBtn, nextBtn);
    const limit = parseInt(track.dataset.limit || '10', 10);
    const handle = track.dataset.handle || 'all';

    whenVisible(root, () => {
      if (track.dataset.loaded === '1') return;
      track.dataset.loaded = '1';
      loadProducts(handle, limit)
        .then((data) => {
          const products = data.products || [];
          track.setAttribute('aria-busy', 'false');
          if (!products.length) return;
          track.innerHTML = products
            .slice(0, limit)
            .map((p) => cardHtml(p, { compactCompare: false }))
            .join('');
          requestAnimationFrame(syncArrows);
        })
        .catch(() => {
          track.dataset.loaded = '0';
          track.setAttribute('aria-busy', 'false');
        });
    });
  };

  const initTimer = (el) => {
    if (!el || el.dataset.nTimerInit === '1') return;
    el.dataset.nTimerInit = '1';
    const hours = Number(el.dataset.hours || 12);
    let total = Math.floor(hours * 3600);
    const fh = el.querySelector('[data-fh]');
    const fm = el.querySelector('[data-fm]');
    const fs = el.querySelector('[data-fs]');
    const pad = (n) => String(n).padStart(2, '0');
    const tick = () => {
      if (total <= 0) total = Math.floor(hours * 3600);
      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      const s = total % 60;
      if (fh) fh.textContent = pad(h);
      if (fm) fm.textContent = pad(m);
      if (fs) fs.textContent = pad(s);
      total -= 1;
    };
    tick();
    setInterval(tick, 1000);
  };

  const boot = () => {
    document.querySelectorAll('[data-n-shelf]').forEach(initShelf);
    document.querySelectorAll('[data-nflash]').forEach(initFlash);
    document.querySelectorAll('[data-nflash-timer]').forEach(initTimer);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
